import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import Search from './Search';
import NewRequestButton from './NewRequestButton';
import ProcurementTable from './ProcurementTable';
import NewRequestModal from './NewRequestModal';
import RFQComparisonModal from './RFQComparisonModal';
import SuccessModal from './SuccessModal';
import { API_ENDPOINTS } from '../utils/config';
import { ProcurementRequest, NewRequestFormData, SupplierQuote } from '../types';
import '../styles/ProcurementSection.css';

// API service for requests
const apiService = {
  async createRequest(requestData: NewRequestFormData, authToken: string) {
    try {
      // Transform custom specifications to the expected format
      const customSpecifications = requestData.custom_specifications?.map(spec => ({
        key: spec.key,
        value: spec.value
      })) || [];

      // Transform custom filters to the expected format
      const customFilters = requestData.customFilters?.reduce((acc, filter) => {
        acc[filter.key] = filter.value;
        return acc;
      }, {} as Record<string, string>) || {};

      // Create payload with all fields including OCR data
      const payload = {
        // Product Information
        product_name: requestData.product_name,
        product_brand: requestData.product_brand || '',
        product_global_code: requestData.product_global_code || '',
        product_company_code: requestData.product_company_code || '',
        product_description: requestData.product_description || '',
        
        // Request Information
        quantity: requestData.quantity || 1,
        unit: requestData.unit || 'piece',
        budget: requestData.budget || 0,
        currency: requestData.currency || 'CHF',
        delivery_time: requestData.delivery_time || '',
        importance: requestData.importance || 'price',
        region: requestData.region || ['CH'],
        category: requestData.category || 'IT',
        address: requestData.address || '',
        
        // Supplier Information
        current_supplier: requestData.current_supplier || '',
        favorite_suppliers: requestData.favorite_suppliers || [],
        selected_approved_suppliers: requestData.selected_approved_suppliers || [],
        
        // Additional Specifications
        additional_specifications: requestData.additional_specifications || [],
        custom_specifications: customSpecifications,
        
        // OCR Information
        is_ocr_generated: requestData.is_ocr_generated || false,
        ocr_data: requestData.ocr_data || {},
        ocr_prices: requestData.ocr_prices || {},
        ocr_supplier_info: requestData.ocr_supplier_info || {},
        
        // Legacy fields for compatibility
        urgency: requestData.urgency || 'normal',
        current_price: requestData.current_price || requestData.currentPrice || 0,
        additional_notes: requestData.additionalNotes || '',
        custom_filters: customFilters
      };
      
      console.log('📤 ProcurementSection - Sending payload:', payload);
      
      const response = await fetch(API_ENDPOINTS.REQUESTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  },

  async getRequests(authToken: string, page: number = 1, limit: number = 50) {
    try {
      const url = new URL(API_ENDPOINTS.REQUESTS);
      url.searchParams.set('page', page.toString());
      url.searchParams.set('limit', limit.toString());
      
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching requests:', error);
      throw error;
    }
  }
};

const ProcurementSection: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  
  // State definitions
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [requestsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState<ProcurementRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNewRequestModalOpen, setIsNewRequestModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<ProcurementRequest | null>(null);
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [selectedRFQRequest, setSelectedRFQRequest] = useState<ProcurementRequest | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: ''
  });
  
  // Function to translate next action values
  const translateNextAction = (action: string) => {
    const actionMap: { [key: string]: string } = {
      'Admin Approval': t('common.next_action_admin_approval'),
      'Send RFQ': t('common.next_action_send_rfq'),
      'Wait for Responses': t('common.next_action_wait_responses'),
      'Evaluate Quotes': t('common.next_action_evaluate_quotes'),
      'Select Supplier': t('common.next_action_select_supplier'),
      'Notify Suppliers': t('common.next_action_notify_suppliers'),
      'Completed': t('common.next_action_completed'),
      'Revision Required': t('common.next_action_revision_required')
    };
    return actionMap[action] || action;
  };
  
  // Fetch real data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const result = await apiService.getRequests(token, currentPage, requestsPerPage);
        console.log('🔍 API result:', result);
        console.log('🔍 Raw request data:', result.data);
        if (result.data && result.data.length > 0) {
          console.log('🔍 First request display_id:', result.data[0].display_id);
          console.log('🔍 First request company_request_id:', result.data[0].company_request_id);
          console.log('🔍 First request _id:', result.data[0]._id);
        }
        
        if (result.success && result.data) {
          // Transform API data to match our interface
          const transformedData = result.data.map((item: any) => {
            // Determine owner name with multiple fallbacks
            let ownerName = 'Unknown';
            
            if (item.user_name) {
              ownerName = item.user_name;
            } else if (item.user_info && item.user_info.name) {
              ownerName = item.user_info.name;
            } else if (item.owner && item.owner !== 'User') {
              ownerName = item.owner;
            } else if (item.user_email) {
              // Extract name from email if available
              const emailName = item.user_email.split('@')[0];
              ownerName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }
            
            return {
              id: item.display_id || item._id || item.id,  // Use display_id (company request ID) if available
              _id: item._id,  // Store the backend ObjectId for internal use
              item: item.product_name,
              category: item.category || item.product_category || 'N/A',
              status: item.phase || item.status, // Use phase if available, fallback to status
              numberOfQuotes: item.number_of_quotes || 0,
              rfqCount: Array.isArray(item.selected_supplier_ids) ? item.selected_supplier_ids.length : (item.rfq_count ?? item.total_emails_sent ?? 0),
              budget: item.budget || 0,  // Add budget field
              currency: item.currency || 'EUR',  // Add currency field
              owner: ownerName,
              date: item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : 'N/A',
              // RFQ Statistics
              total_emails_sent: item.total_emails_sent || 0,
              total_emails_received: item.total_emails_received || 0
            };
          });
          
          console.log('🔍 Transformed data:', transformedData);
          console.log('🔍 Final phase requests:', transformedData.filter((req: ProcurementRequest) => req.status === 'final_phase'));
          
          setData(transformedData);
          
          // Set pagination info
          if (result.pagination) {
            setTotalPages(result.pagination.pages);
            setTotalRequests(result.pagination.total);
          }
        }
      } catch (error) {
        console.error('Error fetching requests:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token, currentPage, requestsPerPage]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    
    const query = searchQuery.toLowerCase();
    return data.filter(request => {
      const requestId = typeof request.id === 'number' ? request.id.toString() : request.id;
      return requestId.toLowerCase().includes(query) ||
        request.item.toLowerCase().includes(query) ||
        request.category.toLowerCase().includes(query) ||
        request.status.toLowerCase().includes(query) ||
        request.owner.toLowerCase().includes(query) ||
        request.date.toLowerCase().includes(query);
    });
  }, [data, searchQuery]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNewRequest = () => {
    setEditingRequest(null);
    setIsNewRequestModalOpen(true);
  };

  const handleRowClick = async (request: ProcurementRequest) => {
    console.log('Row clicked:', request);
    
    // Check if request needs revision
    if (request.status === 'revision_required') {
      // Directly open revision modal without confirmation
      await handleReviseRequest(request);
    } else if (request.status === 'RFQ Running') {
      setSelectedRFQRequest(request);
      setIsRFQModalOpen(true);
    } else if (request.status === 'approved' || request.status === 'select_supplier') {
      // Open supplier selection modal for approved requests
      await handleSelectSupplierRequest(request);
    } else if (request.status === 'final_phase') {
      // Open final phase supplier modal
      await handleFinalPhaseRequest(request);
    }
    // For other statuses, the default behavior (opening detail modal) will be handled by ProcurementTable
  };

  const handleReviseRequest = async (request: ProcurementRequest) => {
    try {
      // Fetch detailed request data from backend
              const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL((request._id || request.id).toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Set the detailed request data for editing
        setEditingRequest(result.data);
        setIsNewRequestModalOpen(true);
      } else {
        console.error('Failed to fetch request details:', result.error);
        alert('Failed to load request details for revision. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching request details for revision:', error);
      alert('Error loading request details for revision. Please try again.');
    }
  };

  const handleRefreshClick = async (request: ProcurementRequest) => {
    try {
      // Fetch detailed request data from backend
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL((request._id || request.id).toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // For re-order, create a new request with the same data but reset some fields
        const reorderData = {
          ...result.data,
          _id: undefined, // Remove ID to create new request
          id: undefined, // Remove ID to create new request
          status: 'document_preparation', // Reset status
          phase: 1, // Reset phase
          created_at: undefined, // Remove creation date
          updated_at: undefined, // Remove update date
          revision_notes: undefined, // Remove revision notes
          revision_requested_by: undefined, // Remove revision info
          revision_requested_at: undefined // Remove revision info
        };
        
        // Set the data for creating a new request (re-order)
        setEditingRequest({...reorderData, isReorder: true});
        setIsNewRequestModalOpen(true);
      } else {
        console.error('Failed to fetch request details:', result.error);
        alert('Failed to load request details for re-order. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching request details for re-order:', error);
      alert('Error loading request details for re-order. Please try again.');
    }
  };

  const handleEditClick = async (request: ProcurementRequest) => {
    console.log('🔍 ProcurementSection - Edit request clicked:', request);
    
    try {
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      // Fetch complete request data from backend
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL((request._id || request.id).toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success) {
        console.log('🔍 ProcurementSection - Complete request data fetched:', result.data);
        setEditingRequest(result.data);
        setIsNewRequestModalOpen(true);
      } else {
        console.error('Failed to fetch request details:', result.error);
        alert('Failed to load request details for editing. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching request details for editing:', error);
      alert('Error loading request details for editing. Please try again.');
    }
  };

  const handleSelectSupplierRequest = async (request: ProcurementRequest) => {
    console.log('🔍 handleSelectSupplierRequest called with:', request);
    try {
      // Fetch detailed request data from backend
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL((request._id || request.id).toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 Request detail response:', result);
      console.log('🔍 Request detail data keys:', Object.keys(result.data || {}));
      console.log('🔍 Request detail selected_suppliers:', result.data?.selected_suppliers);
      console.log('🔍 Request detail selected_suppliers count:', result.data?.selected_suppliers?.length || 0);
      console.log('🔍 Request detail attached_documents:', result.data?.attached_documents);
      console.log('🔍 Request detail attached_documents count:', result.data?.attached_documents?.length || 0);
      
      if (result.success) {
        // Set the detailed request data for supplier selection modal
        const supplierSelectionRequest = {
          ...request,
          selected_suppliers: result.data.selected_suppliers || [],
          attached_documents: result.data.attached_documents || []
        } as ProcurementRequest;
        
        console.log('🔍 Supplier selection request with selected suppliers:', supplierSelectionRequest);
        setSelectedRFQRequest(supplierSelectionRequest);
        setIsRFQModalOpen(true);
      } else {
        console.error('Failed to fetch request details:', result.error);
        alert('Failed to load request details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      alert('Error loading request details. Please try again.');
    }
  };

  const handleFinalPhaseRequest = async (request: ProcurementRequest) => {
    console.log('🔍 handleFinalPhaseRequest called with:', request);
    try {
      // Fetch detailed request data from backend
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL((request._id || request.id).toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 Request detail response:', result);
      console.log('🔍 Request detail data keys:', Object.keys(result.data || {}));
      console.log('🔍 Request detail selected_suppliers:', result.data?.selected_suppliers || []);
      console.log('🔍 Request detail selected_suppliers count:', result.data?.selected_suppliers?.length || 0);
      
      if (result.success) {
        // Set the detailed request data for final phase modal
        const finalPhaseRequest = {
          ...request,
          selected_suppliers: result.data.selected_suppliers || []
        } as ProcurementRequest;
        
        console.log('🔍 Final phase request with selected suppliers:', finalPhaseRequest);
        setSelectedRFQRequest(finalPhaseRequest);
        setIsRFQModalOpen(true);
      } else {
        console.error('Failed to fetch request details:', result.error);
        alert('Failed to load request details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      alert('Error loading request details. Please try again.');
    }
  };

  const handleCloseNewRequestModal = () => {
    setIsNewRequestModalOpen(false);
    setEditingRequest(null);
  };



  const handleSubmitNewRequest = async (formData: NewRequestFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      console.log('🔍 ProcurementSection - Received formData:', formData);
      console.log('🔍 ProcurementSection - formData.product_name:', formData.product_name);
      console.log('🔍 ProcurementSection - formData.product_brand:', formData.product_brand);
      console.log('🔍 ProcurementSection - formData.unit:', formData.unit);
      console.log('🔍 ProcurementSection - formData.quantity:', formData.quantity);
      console.log('🔍 ProcurementSection - formData.budget:', formData.budget);
      console.log('🔍 ProcurementSection - formData keys:', Object.keys(formData));
      console.log('🔍 ProcurementSection - editingRequest:', editingRequest);
      console.log('🔍 ProcurementSection - editingRequest status:', editingRequest?.status);
      
      let result;
      
      // Check if this is a revision update
      if (editingRequest && editingRequest.status === 'revision_required') {
        // Update existing request after revision
        const response = await fetch(API_ENDPOINTS.UPDATE_REVISION((editingRequest._id || editingRequest.id).toString()), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        result = await response.json();
        console.log('Request updated successfully after revision:', result);
        
        // Show success message
        setSuccessModal({
          isOpen: true,
          message: t('common.request_updated_successfully')
        });
      } else if (editingRequest && editingRequest.status !== 'revision_required' && !editingRequest.isReorder) {
        // Update existing request for edit
        const response = await fetch(API_ENDPOINTS.UPDATE_REQUEST((editingRequest._id || editingRequest.id).toString()), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        result = await response.json();
        console.log('Request updated successfully:', result);
        
        // Show success message
        setSuccessModal({
          isOpen: true,
          message: t('common.request_updated_successfully')
        });
      } else {
        // Create new request (either new request or re-order)
        if (editingRequest && editingRequest.isReorder) {
          console.log('🔄 Creating new request from re-order:', editingRequest);
        } else {
          console.log('🆕 Creating new request');
        }
        
        result = await apiService.createRequest(formData, token);
        console.log('Request created successfully:', result);
        
        // Show success message
        const successMessage = editingRequest && editingRequest.isReorder 
          ? t('common.request_created_successfully') // You can add a specific re-order message if needed
          : t('common.request_created_successfully');
        
        console.log('🎉 Setting success modal to open with message:', successMessage);
        setSuccessModal({
          isOpen: true,
          message: successMessage
        });
      }
      
      // Close modal and reset form
      setIsNewRequestModalOpen(false);
      setEditingRequest(null);
      
      // Refresh the data
      const refreshResult = await apiService.getRequests(token);
      if (refreshResult.success && refreshResult.data) {
        const transformedData = refreshResult.data.map((item: any) => {
          // Determine owner name with multiple fallbacks
          let ownerName = 'Unknown';
          
          if (item.user_name) {
            ownerName = item.user_name;
          } else if (item.user_info && item.user_info.name) {
            ownerName = item.user_info.name;
          } else if (item.owner && item.owner !== 'User') {
            ownerName = item.owner;
          } else if (item.user_email) {
            // Extract name from email if available
            const emailName = item.user_email.split('@')[0];
            ownerName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }
          
          return {
            id: item._id || item.id,
            item: item.product_name,
            category: item.category || item.product_category || 'N/A',
            status: item.phase || item.status, // Use phase if available, fallback to status
            numberOfQuotes: item.number_of_quotes || 0,
            rfqCount: Array.isArray(item.selected_supplier_ids) ? item.selected_supplier_ids.length : (item.rfq_count ?? item.total_emails_sent ?? 0),
            owner: ownerName,
            date: item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : 'N/A',
            total_emails_sent: item.total_emails_sent || 0,
            total_emails_received: item.total_emails_received || 0
          };
        });
        setData(transformedData);
      }
      
    } catch (error) {
      console.error('Error submitting request:', error);
      setSubmitError(error instanceof Error ? error.message : t('common.an_error_occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseRFQModal = () => {
    setIsRFQModalOpen(false);
    setSelectedRFQRequest(null);
  };

  const handleSelectSupplier = async (request: ProcurementRequest, selectedSupplier: any) => {
    console.log('🔍 handleSelectSupplier called with:', request, 'selected supplier:', selectedSupplier);
    try {
      if (!token) {
        throw new Error('No authentication token available');
      }

      if (!selectedSupplier) {
        alert('Please select a supplier before completing the request.');
        return;
      }
      
              // Complete the request and calculate savings for selected supplier
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/requests/${(request._id || request.id).toString()}/complete-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            selected_supplier: selectedSupplier,
            completed_at: new Date().toISOString()
          })
        });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        console.log('Request completed successfully:', result);
        setSuccessModal({
          isOpen: true,
          message: t('common.request_completed_successfully')
        });
        
        // Close modal and refresh data
        setIsRFQModalOpen(false);
        setSelectedRFQRequest(null);
        
        // Refresh the data
        const refreshResult = await apiService.getRequests(token);
        if (refreshResult.success && refreshResult.data) {
          const transformedData = refreshResult.data.map((item: any) => {
            // Determine owner name with multiple fallbacks
            let ownerName = 'Unknown';
            
            if (item.user_name) {
              ownerName = item.user_name;
            } else if (item.user_info && item.user_info.name) {
              ownerName = item.user_info.name;
            } else if (item.owner && item.owner !== 'User') {
              ownerName = item.owner;
            } else if (item.user_email) {
              // Extract name from email if available
              const emailName = item.user_email.split('@')[0];
              ownerName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }
            
            return {
              id: item._id || item.id,
              item: item.product_name,
              category: item.category || item.product_category || 'N/A',
              status: item.phase || item.status,
              numberOfQuotes: item.number_of_quotes || 0,
              rfqCount: Array.isArray(item.selected_supplier_ids) ? item.selected_supplier_ids.length : (item.rfq_count ?? item.total_emails_sent ?? 0),
              owner: ownerName,
              date: item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : 'N/A',
              total_emails_sent: item.total_emails_sent || 0,
              total_emails_received: item.total_emails_received || 0
            };
          });
          setData(transformedData);
        }
      } else {
        console.error('Failed to complete request:', result.error);
        alert('Failed to complete request. Please try again.');
      }
    } catch (error) {
      console.error('Error completing request:', error);
      alert('Error completing request. Please try again.');
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading_requests')}</div>;
  }

  return (
    <div className="procurement-section">
      <div className="procurement-header">
        <Search onSearch={handleSearch} placeholder={t('common.search_placeholder')} />
        <NewRequestButton onClick={handleNewRequest} />
      </div>
      <ProcurementTable 
        data={filteredData} 
        onRowClick={handleRowClick}
        onRefreshClick={handleRefreshClick}
        onEditClick={handleEditClick}
      />
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            {t('common.showing')} {((currentPage - 1) * requestsPerPage) + 1} - {Math.min(currentPage * requestsPerPage, totalRequests)} {t('common.of')} {totalRequests} {t('common.requests')}
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {t('common.previous')}
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
      <NewRequestModal
        isOpen={isNewRequestModalOpen}
        onClose={handleCloseNewRequestModal}
        onSubmit={handleSubmitNewRequest}
        editingRequest={editingRequest}
        isSubmitting={isSubmitting}
      />

      <RFQComparisonModal
        isOpen={isRFQModalOpen}
        onClose={handleCloseRFQModal}
        request={selectedRFQRequest}
        suppliers={[]} // Will be fetched from API when needed
        onSelectSupplier={handleSelectSupplier}
      />

      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => {
          setSuccessModal({ isOpen: false, message: '' });
          // Refresh the page after success modal closes
          window.location.reload();
        }}
        message={successModal.message}
        duration={4}
      />
    </div>
  );
};

export default ProcurementSection; 